import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ERouteLinks } from 'models/route';
import { Grid, Typography, Box } from '@mui/material';
import { useTranslations } from 'hooks/useTranslations';
import { Resolver, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import Button from 'components/atoms/Button';
import { EButtonVariants } from 'constants/Buttons';
import { EFormInputTypes, FormInput } from 'components/molecules/FormInput';
import {
  ILicenceTemplateApplicationProcessForm,
  ILicenceTemplateApplicationProcessPriceForm,
  ILicenceTemplateApplicationProcessSettingsForm,
} from 'models/licenceConfigurator';
import { FormWyswigEditor } from 'components/molecules/FormWyswigEditor';
import { useScroll } from 'hooks/useScroll';
import { FormSelect } from 'components/molecules/FormSelect';
import { mapDictionaryToOptionProp } from 'helpers/dictionary';
import { useGlobalProperty } from 'models/globalPropertyContext';
import { useFormPriceSchema } from '../schemaValidation';
import { useConfigurationLicence } from '../hooks';
import { ELicenceFormLabels } from '../helper';
import { StyledValueFormHeader } from './styles';
import { FormRadioGroup } from 'components/molecules/FormRadioGroup';
import { LETypes } from 'constants/LegalEntityTypes';
import { isPathEqualToRouteName } from 'utils/route';
import { EDictionaryTypes } from 'models/dictionary';
import { useLang } from 'models/langContext';

interface IFormLicenceTemplateApplicationProcessPage {
  initialFormValues:
    | ILicenceTemplateApplicationProcessForm
    | ILicenceTemplateApplicationProcessPriceForm
    | ILicenceTemplateApplicationProcessSettingsForm;
  isEdit?: boolean;
  version?: string | null;
  licenceTemplateUniqueId?: string | null;
}

const FormLicenceTemplateApplicationProcessPage = ({
  initialFormValues,
  isEdit = false,
  version = null,
  licenceTemplateUniqueId = null,
}: IFormLicenceTemplateApplicationProcessPage) => {
  const navigate = useNavigate();
  const { pathname }: any = useLocation();
  const isPriceEdit = isPathEqualToRouteName(
    pathname,
    ERouteLinks.EditLicenceTemplateApplicationProcessPricePage,
  );
  const isSettingsEdit = isPathEqualToRouteName(
    pathname,
    ERouteLinks.EditLicenceTemplateApplicationProcessSettingsPage,
  );
  const { schema } = useFormPriceSchema({ isPriceEdit, isSettingsEdit });
  const { getProperties, currency } = useGlobalProperty();
  const [isLoading, setIsLoading] = useState(false);
  const { elRef, scrollToError } = useScroll();
  const { selectedLanguage } = useLang();
  const {
    addLicenceTemplateApplicationProcess,
    editLicenceTemplateApplicationProcessPrice,
    editLicenceTemplateApplicationProcessSettings,
    getLicenceApplicationProcessTypes,
    licenceProcessTypes,
    applicationProcessTypes,
    loadDictionaries,
  } = useConfigurationLicence();
  const { t } = useTranslations();
  const { id = '', processId = '' } = useParams<{ id: string; processId: string }>();

  useEffect(() => {
    (async function init() {
      if (isEdit) {
        await loadDictionaries([EDictionaryTypes.applicationProcessTypes]);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  useEffect(() => {
    (async function init() {
      getProperties();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (async function init() {
      if (id) {
        await getLicenceApplicationProcessTypes(id);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const {
    control,
    handleSubmit,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<ILicenceTemplateApplicationProcessForm>({
    defaultValues: initialFormValues,
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    resolver: yupResolver(schema) as unknown as Resolver<any>,
  });

  useEffect(() => {
    (async () => {
      if (errors) {
        const errorList: any = [];
        const keys = Object.keys(errors);
        keys.map((key) => {
          const items = (errors as Record<string, any[]>)[key];
          // eslint-disable-next-line no-return-assign
          Object.keys(items)?.forEach((k: any) => (errorList[`${key}.${k}`] = items[k]));
          return null;
        });
        scrollToError(errorList);
      }
    })();
  }, [errors]);

  const onSubmitForm = async (data: ILicenceTemplateApplicationProcessForm) => {
    setIsLoading(true);
    let res = null;

    if (isEdit) {
      if (processId && version !== null && isPriceEdit) {
        res = await editLicenceTemplateApplicationProcessPrice(processId, {
          priceName: data.priceName,
          priceFee: data.priceFee,
          priceDescription: data.priceDescription,
          version,
        });
      } else if (processId && version !== null && isSettingsEdit) {
        res = await editLicenceTemplateApplicationProcessSettings(processId, {
          legalEntityType: data.legalEntityType?.toUpperCase() as LETypes,
          comment: data.comment,
          version,
        });
      }
    } else if (licenceTemplateUniqueId) {
      res = await addLicenceTemplateApplicationProcess({
        ...data,
        processTypeUniqueId: data.processTypeUniqueId,
        legalEntityType: data.legalEntityType?.toUpperCase() as LETypes,
        licenceTemplateUniqueId,
      });
    }

    if (res) {
      navigate(
        ERouteLinks.LicenceTemplateApplicationProcessPage.replace(':id', id).replace(
          ':processId',
          isEdit ? processId : res,
        ),
      );
    } else {
      window.scrollTo({ top: 0, behavior: 'auto' });
    }
    setIsLoading(false);
  };

  return (
    <form onSubmit={handleSubmit(onSubmitForm)}>
      <div ref={elRef}>
        <Grid container size={{ xs: 12 }} columnSpacing="20px">
          {(!isEdit || isSettingsEdit) && (
            <>
              <Grid size={{ xs: 12 }} sx={{ my: '20px' }}>
                <StyledValueFormHeader>
                  {t('licence.generalSettings.section')}
                </StyledValueFormHeader>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <FormSelect
                  options={
                    isEdit
                      ? mapDictionaryToOptionProp(applicationProcessTypes)
                      : licenceProcessTypes
                  }
                  name="processTypeUniqueId"
                  setValue={setValue}
                  canBeEmpty
                  label={t(ELicenceFormLabels.processTypes)}
                  control={control}
                  errors={errors}
                  disabled={isEdit}
                  required
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <FormRadioGroup
                  name="legalEntityType"
                  label={t(ELicenceFormLabels.legalEntityType)}
                  options={[
                    {
                      label: t('legalEntity.individual.label'),
                      value: LETypes.individual.toUpperCase(),
                    },
                    {
                      label: t('legalEntity.organisation.label'),
                      value: LETypes.organization.toUpperCase(),
                    },
                  ]}
                  control={control}
                  errors={errors}
                  required
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <FormInput
                  name="comment"
                  label={t(ELicenceFormLabels.comment)}
                  setValue={setValue}
                  control={control}
                  errors={errors}
                />
              </Grid>
            </>
          )}
          {(!isEdit || isPriceEdit) && (
            <>
              <Grid size={{ xs: 12 }} sx={{ my: '20px' }}>
                <StyledValueFormHeader>{t('licence.price.section')}</StyledValueFormHeader>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <FormInput
                  name="priceName"
                  label={t(ELicenceFormLabels.priceName)}
                  setValue={setValue}
                  required
                  control={control}
                  errors={errors}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <FormInput
                  name="priceFee"
                  type={EFormInputTypes.text}
                  InputProps={{
                    inputMode: 'decimal',
                  }}
                  label={t(ELicenceFormLabels.licenceFee)}
                  setValue={setValue}
                  required
                  control={control}
                  errors={errors}
                  endAdornmentText={currency}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <FormWyswigEditor
                  name="priceDescription"
                  label={t(ELicenceFormLabels.priceDescription)}
                  setValue={setValue}
                  control={control}
                  errors={errors}
                  setError={setError}
                  clearErrors={clearErrors}
                  charsLimit={1000}
                  withValidation
                  showCounter
                />
              </Grid>
            </>
          )}
        </Grid>
        <Grid container columnSpacing={3} sx={{ rowGap: { xs: 3, sm: 2 }, mt: 4 }}>
          <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
            <Button
              type="submit"
              fullWidth
              label={t('general.save.button')}
              id="save-button"
              variant={EButtonVariants.contained}
              isLoading={isLoading}
              disabled={isLoading}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
            <Button
              fullWidth
              label={t('general.cancel.button')}
              id="cancel-button"
              variant={EButtonVariants.outlined}
              onClick={() =>
                navigate(
                  isEdit
                    ? ERouteLinks.LicenceTemplateApplicationProcessPage.replace(
                        ':id',
                        id,
                      ).replace(':processId', processId)
                    : ERouteLinks.LicenceTemplatePage.replace(':id', id),
                )
              }
              disabled={isLoading}
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {t('general.mandatoryField.text')}
          </Typography>
        </Box>
      </div>
    </form>
  );
};

export { FormLicenceTemplateApplicationProcessPage };
